<template>
    <div class="notification-card" :class="[notificationType, $mq]" @click="handleCardClick">
        <div class="card-header">
            <div class="sensor-name">
                {{ sensorData.name }}<span v-if="count && Number(count) > 0" class="count"> ({{ count }})</span>
            </div>
            <i class="i-andy-external i-andy-s"></i>
        </div>

        <div class="card-content">
            <div class="temperature" :class="notificationType">{{ formatReading(alertValue && alertValue.value) }}°C</div>
        </div>

        <div class="card-footer" :class="notificationType">
            <div class="icon-container">
                <i v-if="notificationType.includes('incident')" class="i-andy-exclamation i-andy-s"></i>
                <i v-else class="i-andy-menu-task i-andy-s"></i>
            </div>
            <div class="timestamp">
                {{ formatDate(alertIndex) }}
            </div>
        </div>
    </div>
</template>

<script>
import { formatDecimal } from '@/utils/numberFormat'

export default {
    name: 'NotificationCard',
    props: {
        sensorId: {
            type: String,
            required: true
        },
        alertIndex: {
            type: [String, Number],
            required: true
        },
        count: {
            type: [Number, String],
            default: 0
        }
    },
    computed: {
        sensorData() {
            return this.$store.getters['sensor/getSensorValues'](this.sensorId) || {}
        },
        alertValue() {
            if (!this.sensorData || !this.sensorData.values) {
                return { value: 0, timestamp: 0 }
            }
            const value = this.sensorData.values[this.alertIndex]
            return value || { value: 0, timestamp: 0 }
        },
        notificationType() {
            // Returns: 'alert', 'incident-open', or 'incident-closed'
            // If there's an issue property, it's an incident
            if (!this.alertValue || !this.alertValue.issue) {
                return 'alert'
            }

            // It has an issue, so it's an incident
            // Now determine if it's open or closed based on status
            const issueStates = this.$store.getters['getIssueStates']

            // If we don't have issueStates or no status, assume it's open
            if (!issueStates || typeof this.alertValue.issue.status === 'undefined') {
                return 'incident-open'
            }

            const issueState = issueStates[this.alertValue.issue.status]

            // If we can't find the state info, assume it's open
            if (!issueState || typeof issueState.type === 'undefined') {
                return 'incident-open'
            }

            // Check if closed: type === 3 means closed
            const isClosed = issueState.type === 3
            return isClosed ? 'incident-closed' : 'incident-open'
        }
    },
    methods: {
        formatReading(value) {
            if (value === null || value === undefined || value === '') {
                return '0'
            }
            const formatted = formatDecimal(value, this.$i18n.locale)
            return formatted === '' ? '0' : formatted
        },
        formatDate(timestamp) {
            if (!timestamp) return ''
            return moment(timestamp * 1000).format('DD/MM/YYYY - HH:mm') + 'h'
        },
        handleCardClick() {
            // Emit event to parent instead of navigating directly
            // This matches the pattern used by SensorCard
            this.$emit('select', this.sensorId)
        },
        goToIssue() {
            // Navigate to issue detail when clicking "ver incidencia"
            if (this.alertValue.issue && this.alertValue.issue.id) {
                this.$emit('issue-click', this.alertValue.issue.id)
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.notification-card {
    @include display-flex();
    @include flex-direction(column);
    @include border-radius(4px);
    background-color: #fff;
    border: 1px solid $neutro-dark;
    overflow: hidden;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
    cursor: pointer;
    padding: 16px;
    gap: 8px;

    .card-header {
        @include display-flex();
        @include align-items(center);

        .sensor-name {
            @include text-ellipsis();
            font-size: 16px;
            font-family: Avenir-Bold;
            color: $neutro-s90;
            flex: 1;
        }

        i {
            color: $main-light;
            font-size: 18px;
            cursor: pointer;
        }
    }

    .card-content {
        @include display-flex();
        @include justify-content(flex-start);
        @include align-items(center);

        .icon-container {
            flex-shrink: 0;

            i {
                font-size: 32px;
            }
        }

        .temperature {
            font-size: 24px;
            font-family: avenir-bold;
            flex: 1;

            &.alert {
                color: $neutro-s80;
            }

            &.incident-open,
            &.incident-closed {
                color: $error-dark;
            }
        }
    }

    &.alert {
        .icon-container i {
            color: #cc9f00;
            background-color: $accent-t90;
            @include border-radius(2px);
            padding: 4px;
        }
    }

    &.incident-open,
    &.incident-closed {
        .icon-container i {
            color: $error-dark;
            background-color: $error-t90;
            @include border-radius(2px);
            padding: 4px;
        }
    }

    .card-footer {
        @include display-flex();
        @include justify-content(space-between);
        @include align-items(center);
        @include border-radius(4px);

        .timestamp {
            @include font-size(s);
            color: $neutro-s90;
            font-family: $text;
        }

        .issue-link {
            @include display-flex();
            @include align-items(center);
            @include font-size(s);
            gap: 4px;
            color: $error-dark;
            font-family: $text-sbold;
            cursor: pointer;

            white-space: nowrap;

            &:hover {
                color: darken($error-dark, 10%);
            }

            i {
                color: inherit;
                font-size: 14px;
            }
        }
    }

    &.portrait {
        .card-header {
            @include font-size(s);
            padding: 10px 12px;
        }

        .card-content {
            padding: 24px 12px;
            gap: 12px;

            .icon-container i {
                font-size: 28px;
            }

            .temperature {
                @include font-size(xl);
            }
        }

        .card-footer {
            .timestamp,
            .issue-link {
                @include font-size(xs);
            }

            .issue-link i {
                font-size: 12px;
            }
        }
    }
}
</style>
